import { Fragment } from "react";

import { Spinner } from "@/components";
import { analytics } from "@/config/analytics";
import { useCareplanContext } from "@/domains/careplan/contexts/use-careplan-context";
import { useCareplanOrder } from "@/domains/careplan/hooks/use-careplan-order";
import { useRestoreCareplanCartLine } from "@/domains/careplan/hooks/use-restore-careplan-cart-line";
import type { CareplanOrderLine, DraftCart } from "@/domains/careplan/models/types";
import { useUpdateCareplanCartLine } from "@/domains/careplan/mutations/use-update-careplan-cart-line";
import { useCareplanCart } from "@/domains/careplan/queries/use-careplan-cart";
import { useCartSellingPlans } from "@/domains/careplan/queries/use-cart-selling-plans";
import { SUBSCRIPTION_ACTION, SUBSCRIPTION_STATUS } from "@/domains/subscriptions/models/constants";
import type {
  SubscriptionAction,
  SubscriptionStatus as SubscriptionStatusType,
} from "@/domains/subscriptions/models/types";
import { useUpdateSubscriptionStatus } from "@/domains/subscriptions/mutations/use-update-subscription-status";
import { env } from "@/env";
import { ActiveSubscriptionCard } from "@/routes/_private/-components/cart/careplan-cart-active-subscription-card";
import { InactiveSubscriptionCard } from "@/routes/_private/-components/cart/careplan-cart-inactive-subscription-card";
import { PausedSubscriptionCard } from "@/routes/_private/-components/cart/careplan-cart-paused-subscription-card";
import { UpdatedSubscriptionCard } from "@/routes/_private/-components/cart/careplan-cart-updated-subscription-card";
import { CheckoutButton } from "@/routes/_private/-components/cart/checkout-button";
import { SubscriptionManagerButton } from "@/routes/_private/-components/cart/subscription-manager-button";
import { SurveyDiscountBanner } from "@/routes/_private/-components/cart/survey-discount-banner";
import { UpdateSubscriptionModal } from "@/routes/_private/-components/cart/update-subscription-modal";
import type { CareplanCartItem } from "@/stores";
import { isCartProcessing, useCartStore } from "@/stores";
import { Button, icons } from "@/ui";
import { CareplanCartHeader } from "./careplan-cart-header";
import { CareplanCartOrderSummary } from "./careplan-cart-order-summary";
import { CareplanCartSubscriptionSummaryCard } from "./careplan-cart-subscription-summary";
import { CartRecommendedProduct } from "./cart-recommended-product";
import { CartSubscribedProduct } from "./cart-subscribed-product";
import { SubscriptionProcessingState } from "./subscription-processing-state";

type CareplanCartProps = {
  onClose: () => void;
};

export const CareplanCart = ({ onClose }: CareplanCartProps) => {
  const { cart, error, isError } = useCareplanCart();
  const { cartVariantIds, id: careplanId, subscription, wasBuiltFromSurvey } = useCareplanContext();
  const { data: availableSellingPlans } = useCartSellingPlans(cartVariantIds);
  const { order } = useCareplanOrder();
  const updateCareplanCartLineMutation = useUpdateCareplanCartLine();
  const { isPending: isRestoringItem, restore } = useRestoreCareplanCartLine();
  const { isPending: isUpdatingSubscriptionStatus, mutate: updateSubscriptionStatus } =
    useUpdateSubscriptionStatus();
  const recommendedCartLines = useCartStore((state) => {
    return state.recommendedCartLines;
  });
  const isProcessing = useCartStore(isCartProcessing);

  if (!cart || !availableSellingPlans) {
    return (
      <div className="relative flex h-full flex-col items-center justify-center gap-6 p-6">
        {isError ? <p className="text-danger">Error: {error.message}</p> : <Spinner />}
        <Button
          aria-label="Close cart"
          className="absolute top-5 right-5 size-11 rounded-full text-content-muted transition-colors hover:bg-surface-hover hover:text-content"
          onClick={onClose}
          variant="focus"
        >
          <icons.XMarkIcon aria-hidden="true" className="size-7" />
        </Button>
      </div>
    );
  }

  const subscriptionVariantIds = new Set(
    (subscription?.products ?? []).map((p) => {
      return p.variantId;
    }),
  );

  const draftCart: DraftCart = {
    ...cart,
    items: [
      ...(subscription?.products ?? []).map((item) => {
        return {
          ...item,
          source: "subscription" as const,
        };
      }),
      ...(recommendedCartLines ?? [])
        .filter((item) => {
          return item.variantId === undefined || !subscriptionVariantIds.has(item.variantId);
        })
        .map((item) => {
          const currentCartLineItem = cart?.items.find((currentItem) => {
            return currentItem.variantId === item.variantId;
          });

          return {
            ...item,
            id: currentCartLineItem?.id ?? item.id,
            image: currentCartLineItem?.image ?? item.image,
            quantity: currentCartLineItem?.quantity ?? 0,
            isActive: !!currentCartLineItem,
            source: "recommended" as const,
          };
        }),
    ],
    get count() {
      return this.items.reduce((total, item) => {
        return total + item.quantity;
      }, 0);
    },
  };

  const isUpdatingItem = updateCareplanCartLineMutation.isPending;
  const isMutatingLine = isRestoringItem || isUpdatingItem;

  /*
    Before a subscription exists the drawer is the order summary: one row per recommended
    product with a stepper, the ledger and what happens next. Every subscription state keeps
    the layout it had.
  */
  const showOrderSummary = !subscription && !isProcessing && !!order;
  const showSurveyBanner = wasBuiltFromSurvey && !!env.VITE_SURVEY_DISCOUNT_CODE;

  /* The header counts products, not units: "3 products" even when one of them ships as 2. */
  const draftCartProductCount = draftCart.items.filter((item) => {
    return item.quantity > 0;
  }).length;

  const draftCartHasRecommendations = draftCart.items.some((item) => {
    return item.source === "recommended" && item.isActive;
  });

  const handleItemQuantityChange = (item: CareplanCartItem, newQuantity: number) => {
    const shouldRestoreItem = item.quantity === 0 && newQuantity === 1;

    if (shouldRestoreItem) {
      handleRestoreCartLineQuantity(item.merchandiseId);
    } else {
      handleUpdateCartLineQuantity(item.id, newQuantity);
    }
  };

  const handleUpdateCartLineQuantity = (cartLineId: string, quantity: number) => {
    updateCareplanCartLineMutation.mutate({
      cartId: cart.id,
      lines: [
        {
          id: cartLineId,
          quantity,
        },
      ],
    });
  };

  const handleRestoreCartLineQuantity = (merchandiseId: string) => {
    restore(cart.id, { merchandiseId });
  };

  const handleOrderLineQuantityChange = (line: CareplanOrderLine, quantity: number) => {
    if (line.sku) {
      analytics.track("cart_line_quantity_changed", {
        sku: line.sku,
        from: line.quantity,
        to: quantity,
      });
    }

    if (!line.isActive) {
      restore(cart.id, line);

      return;
    }

    handleUpdateCartLineQuantity(line.id, quantity);
  };

  const handleOrderLineRestore = (line: CareplanOrderLine) => {
    if (line.sku) {
      analytics.track("cart_line_restored", { sku: line.sku });
    }

    restore(cart.id, line);
  };

  const subscriptionStatusToComponent: Record<
    SubscriptionStatusType,
    React.ComponentType | typeof Fragment
  > = {
    ACTIVE: ActiveSubscriptionCard,
    INACTIVE: InactiveSubscriptionCard,
    HAS_RECOMMENDATIONS: UpdatedSubscriptionCard,
    NO_SUBSCRIPTION: Fragment,
    CANCELLED: InactiveSubscriptionCard,
    EXPIRED: InactiveSubscriptionCard,
    PAUSED: PausedSubscriptionCard,
    PENDING: UpdatedSubscriptionCard,
  };

  const getStatus = () => {
    if (!subscription) {
      return SUBSCRIPTION_STATUS.NO_SUBSCRIPTION;
    }

    if (draftCartHasRecommendations) {
      return SUBSCRIPTION_STATUS.HAS_RECOMMENDATIONS;
    }

    return subscription.status;
  };

  const SubscriptionStatus = subscriptionStatusToComponent[getStatus()];

  const handleUpdateSubscriptionStatus = (action: SubscriptionAction) => {
    updateSubscriptionStatus({
      careplanId,
      action,
    });
  };

  const getCallToAction = () => {
    if (isProcessing) {
      return (
        <Button className="w-full justify-center" disabled>
          Checkout
        </Button>
      );
    }
    if (!subscription) {
      /* Handled by the order summary footer below. */
      return null;
    }

    if (draftCartHasRecommendations) {
      return <UpdateSubscriptionModal cart={cart} draftCart={draftCart} />;
    }

    if (subscription.status === SUBSCRIPTION_STATUS.PAUSED) {
      return (
        <Button
          className="w-full justify-center"
          isLoading={isUpdatingSubscriptionStatus}
          onClick={() => {
            return handleUpdateSubscriptionStatus(SUBSCRIPTION_ACTION.RESUME);
          }}
        >
          Resume subscription
        </Button>
      );
    }

    return (
      <SubscriptionManagerButton className="w-full justify-center">
        Manage Subscription
      </SubscriptionManagerButton>
    );
  };

  const renderBody = () => {
    if (showOrderSummary) {
      return (
        <CareplanCartOrderSummary
          disabled={isMutatingLine}
          onQuantityChange={handleOrderLineQuantityChange}
          onRestore={handleOrderLineRestore}
          order={order}
          showSurveyBanner={showSurveyBanner}
        />
      );
    }

    return (
      <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-6 py-3.5">
        {isProcessing ? (
          <SubscriptionProcessingState />
        ) : (
          <>
            <SubscriptionStatus />

            {draftCart.items.map((item) => {
              if (item.source === "subscription") {
                return (
                  <CartSubscribedProduct
                    key={item.id}
                    product={item}
                    status={subscription!.status}
                  />
                );
              }

              return (
                <CartRecommendedProduct
                  disabled={isMutatingLine || isUpdatingSubscriptionStatus}
                  key={item.id}
                  onQuantityChange={(quantity) => {
                    return handleItemQuantityChange(item, quantity);
                  }}
                  product={item}
                />
              );
            })}
            {wasBuiltFromSurvey && !!env.VITE_SURVEY_DISCOUNT_CODE ? (
              <SurveyDiscountBanner />
            ) : null}
            <CareplanCartSubscriptionSummaryCard cart={cart} draftCart={draftCart} />
            <p className="text-center text-content-secondary">
              Your recommended products may be changed or updated anytime based on your feedback or
              EO clinical team recommendations.
            </p>
          </>
        )}
      </div>
    );
  };

  const renderFooter = () => {
    if (showOrderSummary) {
      return (
        <div className="flex flex-col items-center gap-[9px] border-t border-line px-4 pt-3 pb-4 sm:px-6 sm:pt-3.5">
          <CheckoutButton
            amountAsString={order.totalAsString}
            checkoutUrl={order.checkoutUrl}
            disabled={isMutatingLine || order.isEmpty}
            isEmpty={order.isEmpty}
          />

          <p className="text-center text-base text-content-secondary">
            30-day satisfaction guarantee. Cancel anytime.
          </p>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-6 border-t px-8 py-6">
        {getCallToAction()}

        <p className="text-center text-sm text-content-secondary">
          Cancel, pause, or modify your
          <br /> subscription anytime. No commitments.
        </p>
      </div>
    );
  };

  return (
    <div className="flex h-full flex-col">
      <CareplanCartHeader
        cartCount={showOrderSummary ? order.productCount : draftCartProductCount}
        onClose={onClose}
      />
      {renderBody()}
      {renderFooter()}
    </div>
  );
};
